import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
import api from "../../../shared/api/apiClient";
import useChatSocket from "../hooks/useChatSocket";
import useDraggableFloating from "../hooks/useDraggableFloating";
import { useChat } from "../context/ChatContext";
import styles from "./MiniChatWindow.module.css";
import EmojiPicker from "emoji-picker-react";

const PAGE_SIZE = 30;
const TAB_LIST = "list";
const TAB_CHAT = "chat";

function safeDecodeToken() {
    try {
        const t = localStorage.getItem("token");
        if (!t) return null;
        return jwtDecode(t);
    } catch {
        return null;
    }
}

function formatTime(dt) {
    if (!dt) return "";
    try {
        return new Date(dt).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "";
    }
}

function initials(name) {
    const s = (name || "").trim();
    if (!s) return "C";
    const parts = s.split(" ").filter(Boolean);
    const a = (parts[0] || "").slice(0, 1).toUpperCase();
    const b = (parts[1] || "").slice(0, 1).toUpperCase();
    return (a + b) || a || "C";
}

function isNearBottom(containerEl, thresholdPx = 120) {
    if (!containerEl) return true;
    const { scrollTop, scrollHeight, clientHeight } = containerEl;
    return scrollHeight - (scrollTop + clientHeight) < thresholdPx;
}

function shortenMessage(text, fallback, max = 42) {
    const value = String(text || "").replace(/\s+/g, " ").trim();
    if (!value) return fallback;
    return value.length > max ? `${value.slice(0, max)}...` : value;
}

export default function FloatingChatLauncher() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const {
        unreadCount,
        unreadMap,
        presenceMap = {},
        clearUnread,
        setActiveChatThread,
        setChatPageOpened,
        lastEvent,
        notifications,
    } = useChat();

    const getThreadTitle = useCallback(
        (thread, myId) => {
            if (!thread) return "";
            if (thread.type === "group") return thread.title || t("chat_group_chat");

            const parts = Array.isArray(thread.participants) ? thread.participants : [];
            const other = parts.find((p) => Number(p.id) !== Number(myId));
            return other?.full_name || thread.title || t("chat_direct_chat");
        },
        [t]
    );

    const getThreadSubtitle = useCallback(
        (thread, myId, globalPresenceMap, localPresence) => {
            if (!thread) return t("chat_no_active_chat");

            if (thread.type === "group") {
                const count = Array.isArray(thread.participants) ? thread.participants.length : 0;
                return `${count} ${count === 1 ? t("chat_participant") : t("chat_participants")}`;
            }

            const parts = Array.isArray(thread.participants) ? thread.participants : [];
            const other = parts.find((p) => Number(p.id) !== Number(myId));
            if (!other) return t("chat_direct_chat");

            const status =
                globalPresenceMap?.[Number(other.id)] ||
                localPresence?.[Number(other.id)] ||
                "offline";

            return status === "online" ? t("chat_online") : t("chat_offline");
        },
        [t]
    );

    const me = useMemo(() => {
        const decoded = safeDecodeToken();
        const myId = decoded?.id ? Number(decoded.id) : null;
        return {
            id: myId,
            full_name: decoded?.full_name || t("chat_me"),
            email: decoded?.email || "",
        };
    }, [t]);

    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(TAB_LIST);
    const [attention, setAttention] = useState(false);
    const [toastVisible, setToastVisible] = useState(false);
    const [toastData, setToastData] = useState(null);

    const isMobile = window.innerWidth <= 768;

const getDefaultDesktopPosition = () => {
    const collapsed =
        document.body.classList.contains("sidebar-collapsed") ||
        document.querySelector("[data-sidebar='collapsed']");

    return {
        x: collapsed ? 16 : 220,
        y: window.innerHeight - 120,
    };
};

const launcherDrag = useDraggableFloating({
    width: 64,
    height: 64,
    enabled: !isOpen && !isMobile,
    defaultPosition: isMobile
        ? {
              x: 16,
              y: window.innerHeight - 90,
          }
        : getDefaultDesktopPosition(),
});

const windowDrag = useDraggableFloating({
    width: 350,
    height: 560,
    enabled: isOpen && !isMobile,
    defaultPosition: isMobile
        ? {
              x: 12,
              y: 80,
          }
        : {
              x: window.innerWidth - 390,
              y: window.innerHeight - 650,
          },
});

const drag = isOpen ? windowDrag : launcherDrag;

    const [threads, setThreads] = useState([]);
    const [threadsLoading, setThreadsLoading] = useState(false);
    const [threadsError, setThreadsError] = useState("");

    const [activeThreadId, setActiveThreadIdLocal] = useState(null);

    const activeThread = useMemo(
        () => threads.find((th) => Number(th.id) === Number(activeThreadId)) || null,
        [threads, activeThreadId]
    );

    const [messages, setMessages] = useState([]);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [messagesError, setMessagesError] = useState("");

    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [showEmoji, setShowEmoji] = useState(false);

    const [search, setSearch] = useState("");
    const [typingUsers, setTypingUsers] = useState({});
    const [readMap, setReadMap] = useState({});
    const [presence, setPresence] = useState({});
    const [justOpenedUnreadIds, setJustOpenedUnreadIds] = useState(new Set());

    const listRef = useRef(null);
    const bottomRef = useRef(null);
    const activeThreadIdRef = useRef(activeThreadId);
    const emojiPickerRef = useRef(null);
    const emojiButtonRef = useRef(null);
    const typingTimerRef = useRef(null);
    const lastTypingSentRef = useRef(0);
    const attentionTimerRef = useRef(null);
    const titleTimerRef = useRef(null);
    const toastHideTimerRef = useRef(null);
    const toastUnmountTimerRef = useRef(null);
    const prevUnreadRef = useRef(unreadCount);
    const prevNotificationsLenRef = useRef(Array.isArray(notifications) ? notifications.length : 0);

    const audioRef = useRef(
        typeof Audio !== "undefined" ? new Audio("/notification.mp3") : null
    );

    const TYPING_THROTTLE_MS = 700;
    const TYPING_STOP_MS = 1500;
    const originalTitleRef = useRef(typeof document !== "undefined" ? document.title : "");

    useEffect(() => {
        activeThreadIdRef.current = activeThreadId;
    }, [activeThreadId]);

    useEffect(() => {
        const unlockAudio = () => {
            const audio = audioRef.current;
            if (!audio) return;

            try {
                audio.volume = 0;
                const p = audio.play();

                if (p && typeof p.then === "function") {
                    p.then(() => {
                        audio.pause();
                        audio.currentTime = 0;
                        audio.volume = 1;
                    }).catch(() => {});
                }
            } catch {}
        };

        window.addEventListener("click", unlockAudio, { once: true });
        return () => {
            window.removeEventListener("click", unlockAudio);
        };
    }, []);

    const playNotificationSound = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;

        try {
            audio.currentTime = 0;
            const p = audio.play();
            if (p && typeof p.catch === "function") {
                p.catch(() => {});
            }
        } catch {}
    }, []);

    const blinkTitle = useCallback(
        (textValue) => {
            if (typeof document === "undefined") return;

            if (titleTimerRef.current) {
                clearInterval(titleTimerRef.current);
                titleTimerRef.current = null;
            }

            const original = originalTitleRef.current || document.title || t("chat_app_title");
            let toggle = false;

            titleTimerRef.current = setInterval(() => {
                toggle = !toggle;
                document.title = toggle ? textValue : original;
            }, 900);

            setTimeout(() => {
                if (titleTimerRef.current) {
                    clearInterval(titleTimerRef.current);
                    titleTimerRef.current = null;
                }
                document.title = original;
            }, 7000);
        },
        [t]
    );

    const showBrowserNotification = useCallback((title, body) => {
        if (typeof window === "undefined" || !("Notification" in window)) return;

        if (Notification.permission === "granted") {
            try {
                new Notification(title, {
                    body: body || t("chat_new_message"),
                    silent: false,
                });
            } catch {}
        }
    }, [t]);

    const requestBrowserNotificationPermission = useCallback(() => {
        if (typeof window === "undefined" || !("Notification" in window)) return;
        if (Notification.permission === "default") {
            Notification.requestPermission().catch(() => {});
        }
    }, []);

    const triggerAttention = useCallback(() => {
        setAttention(true);

        if (attentionTimerRef.current) {
            clearTimeout(attentionTimerRef.current);
        }

        attentionTimerRef.current = setTimeout(() => {
            setAttention(false);
        }, 6000);
    }, []);

    const showSideToast = useCallback(
        (senderName, body, threadId) => {
            if (toastHideTimerRef.current) clearTimeout(toastHideTimerRef.current);
            if (toastUnmountTimerRef.current) clearTimeout(toastUnmountTimerRef.current);

            setToastData({
                sender: senderName || t("chat_new_message"),
                body: shortenMessage(body, t("chat_new_message")),
                threadId: Number(threadId) || null,
            });
            setToastVisible(true);

            toastHideTimerRef.current = setTimeout(() => {
                setToastVisible(false);
            }, 5600);

            toastUnmountTimerRef.current = setTimeout(() => {
                setToastData(null);
            }, 6200);
        },
        [t]
    );

    useEffect(() => {
        requestBrowserNotificationPermission();
    }, [requestBrowserNotificationPermission]);

    useEffect(() => {
        return () => {
            if (attentionTimerRef.current) clearTimeout(attentionTimerRef.current);
            if (titleTimerRef.current) clearInterval(titleTimerRef.current);
            if (toastHideTimerRef.current) clearTimeout(toastHideTimerRef.current);
            if (toastUnmountTimerRef.current) clearTimeout(toastUnmountTimerRef.current);

            if (typeof document !== "undefined" && originalTitleRef.current) {
                document.title = originalTitleRef.current;
            }
        };
    }, []);

    const notifyIncoming = useCallback(
        ({ title, body, threadId, senderId }) => {
            const fromMe = me.id && Number(senderId) === Number(me.id);
            if (fromMe) return;

            const isViewingSameThread =
                isOpen &&
                activeTab === TAB_CHAT &&
                Number(activeThreadIdRef.current) === Number(threadId);

            if (isViewingSameThread) return;

            playNotificationSound();
            triggerAttention();
            blinkTitle(`${t("chat_new_message")} — ${title || t("chat_chats")}`);
            showBrowserNotification(title || t("chat_new_message"), body || "");
            showSideToast(title || t("chat_new_message"), body || "", threadId);
        },
        [
            activeTab,
            blinkTitle,
            isOpen,
            me.id,
            playNotificationSound,
            showBrowserNotification,
            showSideToast,
            triggerAttention,
            t,
        ]
    );

    useEffect(() => {
        if (!lastEvent || lastEvent.type !== "message" || !lastEvent.message) return;

        const msg = lastEvent.message;
        notifyIncoming({
            title: msg?.sender?.full_name || t("chat_new_message"),
            body: msg?.body || "",
            threadId: msg?.thread_id,
            senderId: msg?.sender_id,
        });
    }, [lastEvent, notifyIncoming, t]);

    useEffect(() => {
        const len = Array.isArray(notifications) ? notifications.length : 0;
        const prevLen = prevNotificationsLenRef.current;

        if (len > prevLen && len > 0) {
            const latest = notifications[0];
            notifyIncoming({
                title: latest?.title || t("chat_new_message"),
                body: latest?.body || "",
                threadId: latest?.threadId,
                senderId: latest?.sender?.id,
            });
        }

        prevNotificationsLenRef.current = len;
    }, [notifications, notifyIncoming, t]);

    useEffect(() => {
        const prevUnread = Number(prevUnreadRef.current || 0);
        const currentUnread = Number(unreadCount || 0);

        if (currentUnread > prevUnread) {
            triggerAttention();
        }

        prevUnreadRef.current = currentUnread;
    }, [unreadCount, triggerAttention]);

    useEffect(() => {
        if (!isOpen) {
            setChatPageOpened?.(false);
            setActiveChatThread?.(null);
            return;
        }

        setChatPageOpened?.(true);
        setActiveChatThread?.(activeThreadId ? Number(activeThreadId) : null);

        return () => {
            setChatPageOpened?.(false);
            setActiveChatThread?.(null);
        };
    }, [isOpen, activeThreadId, setActiveChatThread, setChatPageOpened]);

    const scrollToBottom = (smooth = true) => {
        const el = bottomRef.current;
        if (!el) return;
        try {
            el.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "end" });
        } catch {}
    };

    const loadThreads = useCallback(async () => {
        setThreadsLoading(true);
        setThreadsError("");

        try {
            const { data } = await api.get("/chat/threads");
            const items = Array.isArray(data) ? data : [];
            setThreads(items);

            setActiveThreadIdLocal((prev) => {
                if (prev && items.some((x) => Number(x.id) === Number(prev))) {
                    return prev;
                }
                return items.length ? items[0].id : null;
            });
        } catch (e) {
            setThreadsError(e?.response?.data?.detail || t("chat_failed_load_chats"));
        } finally {
            setThreadsLoading(false);
        }
    }, [t]);

    const loadMessages = useCallback(
        async (threadId, { markAsRead = false } = {}) => {
            if (!threadId) return;

            setMessagesLoading(true);
            setMessagesError("");

            try {
                const { data } = await api.get(`/chat/threads/${threadId}/messages`, {
                    params: { limit: PAGE_SIZE },
                });

                const items = Array.isArray(data?.items) ? data.items : [];
                const unreadForThread = Number(unreadMap?.[Number(threadId)] || 0);

                if (unreadForThread > 0 && items.length > 0) {
                    const unreadSlice = items.slice(-unreadForThread);
                    setJustOpenedUnreadIds(new Set(unreadSlice.map((m) => Number(m.id))));
                } else {
                    setJustOpenedUnreadIds(new Set());
                }

                setMessages(items);

                requestAnimationFrame(() => scrollToBottom(false));

                const last = items.length ? items[items.length - 1] : null;
                if (markAsRead && last?.id) {
                    api
                        .post(`/chat/threads/${threadId}/read`, {
                            last_read_message_id: last.id,
                        })
                        .catch(() => {});

                    clearUnread?.(Number(threadId));
                    setThreads((prev) =>
                        prev.map((th) =>
                            Number(th.id) === Number(threadId) ? { ...th, unread_count: 0 } : th
                        )
                    );
                }
            } catch (e) {
                setMessagesError(e?.response?.data?.detail || t("chat_failed_load_messages"));
            } finally {
                setMessagesLoading(false);
            }
        },
        [clearUnread, unreadMap, t]
    );

    useEffect(() => {
        if (!isOpen) return;
        loadThreads();
    }, [isOpen, loadThreads]);

    useEffect(() => {
        if (!isOpen || !activeThreadId || activeTab !== TAB_CHAT) return;
        loadMessages(activeThreadId, { markAsRead: true });
    }, [isOpen, activeThreadId, activeTab, loadMessages]);

    useEffect(() => {
        if (!showEmoji) return;

        const handleOutsideClick = (event) => {
            const pickerEl = emojiPickerRef.current;
            const buttonEl = emojiButtonRef.current;

            if (pickerEl?.contains(event.target)) return;
            if (buttonEl?.contains(event.target)) return;

            setShowEmoji(false);
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [showEmoji]);

    useEffect(() => {
        setShowEmoji(false);
    }, [activeThreadId, activeTab]);

    useEffect(() => {
        if (!isOpen) {
            setShowEmoji(false);
        }
    }, [isOpen]);

    const openThread = useCallback((threadId) => {
        const tid = Number(threadId);
        setActiveThreadIdLocal(tid);
        setTypingUsers({});
        setActiveTab(TAB_CHAT);
        setAttention(false);
    }, []);

    const openThreadFromToast = useCallback((threadId) => {
        const tid = Number(threadId);
        if (!tid) return;

        setActiveThreadIdLocal(tid);
        setTypingUsers({});
        setActiveTab(TAB_CHAT);
        setIsOpen(true);
        setAttention(false);
        setToastVisible(false);

        if (toastUnmountTimerRef.current) clearTimeout(toastUnmountTimerRef.current);
        toastUnmountTimerRef.current = setTimeout(() => {
            setToastData(null);
        }, 220);
    }, []);

    const onEvent = useCallback(
        (evt) => {
            if (!evt?.type) return;
            if (evt.type === "pong") return;

            if (evt.type === "presence") {
                const uid = Number(evt.user_id);
                if (!uid) return;
                setPresence((p) => ({ ...p, [uid]: evt.status === "online" ? "online" : "offline" }));
                return;
            }

            if (evt.type === "typing") {
                const uid = Number(evt.user_id);
                if (!uid) return;

                const isTyping = !!evt.is_typing;
                setTypingUsers((p) => ({ ...p, [uid]: isTyping }));

                if (isTyping) {
                    setTimeout(() => {
                        setTypingUsers((prev) => ({ ...prev, [uid]: false }));
                    }, 3000);
                }
                return;
            }

            if (evt.type === "read_state") {
                const threadId = Number(evt.thread_id);
                const reads = evt.reads || {};
                if (!threadId) return;

                const normalized = {};
                for (const [k, v] of Object.entries(reads)) {
                    const uid = Number(k);
                    if (!uid) continue;
                    normalized[uid] = Number(v || 0);
                }

                setReadMap((prev) => ({
                    ...prev,
                    [threadId]: {
                        ...(prev[threadId] || {}),
                        ...normalized,
                    },
                }));
                return;
            }

            if (evt.type === "read") {
                const threadId = Number(evt.thread_id);
                const uid = Number(evt.user_id);
                const lastRead = Number(evt.last_read_message_id || 0);
                if (!threadId || !uid) return;

                setReadMap((prev) => {
                    const current = Number(prev?.[threadId]?.[uid] || 0);
                    if (lastRead <= current) return prev;

                    return {
                        ...prev,
                        [threadId]: {
                            ...(prev[threadId] || {}),
                            [uid]: lastRead,
                        },
                    };
                });
                return;
            }

            if (evt.type === "message") {
                const msg = evt.message;
                if (!msg?.id || !msg?.thread_id) return;

                const msgThreadId = Number(msg.thread_id);
                const currentThreadId = activeThreadIdRef.current
                    ? Number(activeThreadIdRef.current)
                    : null;
                const fromMe = me.id && Number(msg.sender_id) === Number(me.id);

                setThreads((prev) => {
                    const idx = prev.findIndex((th) => Number(th.id) === msgThreadId);
                    if (idx === -1) return prev;

                    const thread = prev[idx];
                    const isActiveChatTab =
                        currentThreadId &&
                        currentThreadId === msgThreadId &&
                        activeTab === TAB_CHAT;

                    const updated = {
                        ...thread,
                        last_message: msg.body,
                        last_message_at: msg.created_at,
                        unread_count:
                            isActiveChatTab || fromMe ? 0 : Number(thread.unread_count || 0) + 1,
                    };

                    const copy = [...prev];
                    copy.splice(idx, 1);
                    return [updated, ...copy];
                });

                if (fromMe) return;

                if (currentThreadId && currentThreadId === msgThreadId && activeTab === TAB_CHAT) {
                    const shouldStick = isNearBottom(listRef.current);

                    setMessages((prev) => {
                        if (prev.some((m) => String(m.id) === String(msg.id))) return prev;
                        return [...prev, msg];
                    });

                    setJustOpenedUnreadIds((prev) => {
                        const next = new Set(prev);
                        next.add(Number(msg.id));
                        return next;
                    });

                    api
                        .post(`/chat/threads/${msgThreadId}/read`, {
                            last_read_message_id: msg.id,
                        })
                        .catch(() => {});

                    clearUnread?.(msgThreadId);

                    setThreads((prev) =>
                        prev.map((th) =>
                            Number(th.id) === msgThreadId ? { ...th, unread_count: 0 } : th
                        )
                    );

                    if (shouldStick) {
                        requestAnimationFrame(() => scrollToBottom(true));
                    }

                    setTimeout(() => {
                        setJustOpenedUnreadIds((prev) => {
                            const next = new Set(prev);
                            next.delete(Number(msg.id));
                            return next;
                        });
                    }, 3500);
                }
            }
        },
        [clearUnread, me.id, activeTab]
    );

    const { send: wsSend } = useChatSocket({
        threadId: activeThreadId,
        onEvent,
    });

    const filteredThreads = useMemo(() => {
        const q = (search || "").trim().toLowerCase();
        if (!q) return threads;

        return threads.filter((th) => {
            const title = (getThreadTitle(th, me.id) || "").toLowerCase();
            const last = (th.last_message || "").toLowerCase();
            const names = (Array.isArray(th.participants) ? th.participants : [])
                .map((p) => (p.full_name || "").toLowerCase())
                .join(" ");

            return title.includes(q) || last.includes(q) || names.includes(q);
        });
    }, [threads, search, me.id, getThreadTitle]);

    const typingList = useMemo(() => {
        const parts = Array.isArray(activeThread?.participants)
            ? activeThread.participants
            : [];

        return parts
            .filter((p) => Number(p.id) !== Number(me.id) && typingUsers[p.id])
            .map((p) => p.full_name || `${t("log_user")} ${p.id}`);
    }, [activeThread, me.id, typingUsers, t]);

    const participants = Array.isArray(activeThread?.participants)
        ? activeThread.participants
        : [];

    const otherIds = participants
        .map((p) => Number(p.id))
        .filter((id) => id && id !== Number(me.id));

    const sendMessage = async () => {
        const body = (text || "").trim();
        if (!body || !activeThreadId || sending) return;

        setSending(true);
        setMessagesError("");

        if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
        wsSend({ type: "typing", is_typing: false });

        const tempId = `tmp_${Date.now()}`;
        const optimistic = {
            id: tempId,
            thread_id: activeThreadId,
            body,
            created_at: new Date().toISOString(),
            sender_id: me.id,
            sender: {
                id: me.id,
                full_name: me.full_name,
                email: me.email,
            },
            is_deleted: false,
            edited_at: null,
        };

        setMessages((prev) => [...prev, optimistic]);
        setText("");
        requestAnimationFrame(() => scrollToBottom(true));

        try {
            const { data } = await api.post(`/chat/threads/${activeThreadId}/messages`, {
                body,
            });

            setMessages((prev) => prev.map((m) => (m.id === tempId ? data : m)));

            setThreads((prev) =>
                prev.map((th) =>
                    Number(th.id) === Number(activeThreadId)
                        ? {
                              ...th,
                              last_message: data?.body,
                              last_message_at: data?.created_at,
                              unread_count: 0,
                          }
                        : th
                )
            );

            clearUnread?.(Number(activeThreadId));

            if (data?.id) {
                api
                    .post(`/chat/threads/${activeThreadId}/read`, {
                        last_read_message_id: data.id,
                    })
                    .catch(() => {});
            }
        } catch (e) {
            setMessages((prev) => prev.filter((m) => m.id !== tempId));
            setMessagesError(e?.response?.data?.detail || t("chat_failed_send_message"));
            setText(body);
        } finally {
            setSending(false);
        }
    };

    const onEnterSend = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const launcherUnread = unreadCount > 99 ? "99+" : unreadCount;
    const activeSubtitle = getThreadSubtitle(activeThread, me.id, presenceMap, presence);

    const onEmojiClick = useCallback((emojiData) => {
        setText((prev) => `${prev}${emojiData.emoji}`);
        setShowEmoji(false);
    }, []);

    return (
        <div
            className={styles.floatingRoot}
            style={{
                left: `${drag.position.x}px`,
                top: `${drag.position.y}px`,
            }}
        >
            {!isOpen ? (
                <div className={styles.launcherDock}>
                    <button
                        type="button"
                        className={`${styles.launcher} ${styles.dragHint} ${styles.launcherBell} ${
                            unreadCount > 0 ? styles.launcherUnreadPersistent : ""
                        } ${attention ? styles.launcherAttention : ""} ${
                            toastData && toastVisible ? styles.launcherConnected : ""
                        }`}
                        onMouseDown={drag.bindDragHandle}
                        onClick={() => {
                            setIsOpen(true);
                            setAttention(false);
                        }}
                        title={t("chat_notifications_and_chat")}
                    >
                        <i style={{ fontSize: "30px" }} className={`lni lni-comment-1-text ${styles.launcherBellIcon}`} />
                        {unreadCount > 0 && (
                            <span className={styles.launcherBadge}>{launcherUnread}</span>
                        )}
                    </button>

                    {toastData && (
                        <button
                            type="button"
                            className={`${styles.sideToast} ${
                                toastVisible ? styles.sideToastVisible : styles.sideToastHidden
                            } ${toastVisible ? styles.sideToastConnected : ""}`}
                            onClick={() => openThreadFromToast(toastData.threadId)}
                            title={t("chat_open_latest_message")}
                        >
                            <div className={styles.sideToastTitle}>{toastData.sender}</div>
                            <div className={styles.sideToastBody}>{toastData.body}</div>
                        </button>
                    )}
                </div>
            ) : (
                <div className={`${styles.window} ${attention ? styles.windowAttention : ""}`}>
                    <div
                        className={`${styles.header} ${styles.dragHint}`}
                        onMouseDown={drag.bindDragHandle}
                    >
                        <div className={styles.headerLeft}>
                            <div className={styles.headerAvatar}>
                                {initials(activeThread ? getThreadTitle(activeThread, me.id) : t("chat_chats"))}
                            </div>

                            <div className={styles.headerText}>
                                <div className={styles.headerTitle}>
                                    {activeThread ? getThreadTitle(activeThread, me.id) : t("chat_messenger")}
                                </div>
                                <div className={styles.headerSub}>{activeSubtitle}</div>
                            </div>
                        </div>

                        <div className={styles.headerActions}>
                            <button
                                type="button"
                                className={styles.iconBtn}
                                title={t("chat_open_full_chat")}
                                onClick={() => {
                                    setIsOpen(false);
                                    navigate("/menu/chat");
                                }}
                            >
                                <i className="lni lni-expand-square-4"></i>
                            </button>

                            <button
                                type="button"
                                className={styles.iconBtn}
                                title={t("chat_close")}
                                onClick={() => setIsOpen(false)}
                            >
                                <i className="lni lni-xmark"></i>
                            </button>
                        </div>
                    </div>

                    <div className={styles.tabsWrap}>
                        <button
                            type="button"
                            className={`${styles.tabBtn} ${activeTab === TAB_LIST ? styles.tabBtnActive : ""}`}
                            onClick={() => setActiveTab(TAB_LIST)}
                        >
                            {t("chat_chats")}
                        </button>

                        <button
                            type="button"
                            className={`${styles.tabBtn} ${activeTab === TAB_CHAT ? styles.tabBtnActive : ""}`}
                            onClick={() => setActiveTab(TAB_CHAT)}
                            disabled={!activeThread}
                        >
                            {activeThread ? getThreadTitle(activeThread, me.id) : t("chat_direct_chat")}
                        </button>
                    </div>

                    {threadsError && <div className={styles.alertError}>{threadsError}</div>}
                    {messagesError && <div className={styles.alertError}>{messagesError}</div>}

                    {activeTab === TAB_LIST ? (
                        <div className={styles.tabPanel}>
                            <div className={styles.topTools}>
                                <input
                                    className={styles.searchInput}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder={t("chat_search_chats")}
                                />
                            </div>

                            <div className={styles.threadListFull}>
                                {threadsLoading ? (
                                    <div className={styles.hint}>{t("chat_loading_chats")}</div>
                                ) : filteredThreads.length ? (
                                    filteredThreads.map((th) => {
                                        const threadIdNum = Number(th.id);
                                        const isActive = threadIdNum === Number(activeThreadId);

                                        const unread =
                                            unreadMap && Object.prototype.hasOwnProperty.call(unreadMap, threadIdNum)
                                                ? Number(unreadMap[threadIdNum] || 0)
                                                : Number(th.unread_count || 0);

                                        return (
                                            <button
                                                key={th.id}
                                                type="button"
                                                className={`${styles.threadItemLarge} ${isActive ? styles.threadItemActive : ""}`}
                                                onClick={() => openThread(th.id)}
                                            >
                                                <div className={styles.threadAvatarLarge}>
                                                    {initials(getThreadTitle(th, me.id))}
                                                </div>

                                                <div className={styles.threadBodyLarge}>
                                                    <div className={styles.threadTopLine}>
                                                        <div className={styles.threadName}>
                                                            {getThreadTitle(th, me.id)}
                                                        </div>
                                                        <div className={styles.threadTime}>
                                                            {formatTime(th.last_message_at)}
                                                        </div>
                                                    </div>

                                                    <div className={styles.threadBottomLine}>
                                                        <div className={styles.threadPreviewLarge}>
                                                            {th.last_message || t("chat_no_messages_yet")}
                                                        </div>

                                                        {unread > 0 && (
                                                            <div className={styles.unreadBadge}>
                                                                {unread > 99 ? "99+" : unread}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })
                                ) : (
                                    <div className={styles.hint}>{t("chat_no_chats_found")}</div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className={styles.tabPanel}>
                            {!activeThread ? (
                                <div className={styles.emptyState}>
                                    <div className={styles.emptyTitle}>{t("chat_select_a_chat")}</div>
                                    <div className={styles.emptySub}>
                                        {t("chat_open_chats_tab_and_choose")}
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.chatBody}>
                                    <div className={styles.messages} ref={listRef}>
                                        {messagesLoading && messages.length === 0 && (
                                            <div className={styles.hint}>{t("chat_loading_messages")}</div>
                                        )}

                                        {messages.map((m) => {
                                            const mine = me.id && Number(m.sender_id) === Number(me.id);
                                            const senderName = m.sender?.full_name || (mine ? t("chat_me") : t("chat_unknown"));
                                            const isUnreadHighlight =
                                                !mine && justOpenedUnreadIds.has(Number(m.id));

                                            const seen =
                                                mine &&
                                                otherIds.some((uid) => {
                                                    const lastRead = Number(readMap?.[Number(activeThreadId)]?.[uid] || 0);
                                                    return lastRead >= Number(m.id);
                                                });

                                            return (
                                                <div
                                                    key={m.id}
                                                    className={mine ? styles.msgRowMine : styles.msgRowTheirs}
                                                >
                                                    <div
                                                        className={`${styles.msgBubble} ${
                                                            isUnreadHighlight ? styles.msgBubbleUnread : ""
                                                        }`}
                                                    >
                                                        <div className={styles.msgMeta}>
                                                            {mine && (
                                                                <span
                                                                    className={`${styles.tick} ${seen ? styles.tickSeen : ""}`}
                                                                    title={seen ? t("chat_seen") : t("chat_sent")}
                                                                >
                                                                    {seen ? "✓✓" : "✓"}
                                                                </span>
                                                            )}
                                                            <span className={styles.msgSender}>{senderName}</span>
                                                            <span className={styles.msgTime}>
                                                                {formatTime(m.created_at)}
                                                            </span>
                                                        </div>

                                                        <div className={styles.msgBody}>
                                                            {m.is_deleted ? t("chat_message_deleted") : m.body}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        <div ref={bottomRef} />
                                    </div>

                                    {!!typingList.length && (
                                        <div className={styles.typingWrap}>
                                            {typingList.join(", ")} {t("chat_typing")}
                                        </div>
                                    )}

                                    <div className={styles.composer}>
                                        <div className={styles.inputShell}>
                                            <textarea
                                                className={styles.textarea}
                                                value={text}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setText(v);

                                                    const now = Date.now();
                                                    if (now - lastTypingSentRef.current > TYPING_THROTTLE_MS) {
                                                        lastTypingSentRef.current = now;
                                                        wsSend({ type: "typing", is_typing: true });
                                                    }

                                                    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
                                                    typingTimerRef.current = setTimeout(() => {
                                                        wsSend({ type: "typing", is_typing: false });
                                                    }, TYPING_STOP_MS);
                                                }}
                                                onKeyDown={onEnterSend}
                                                placeholder={t("chat_write_message")}
                                                disabled={sending}
                                            />

                                            <button
                                                ref={emojiButtonRef}
                                                type="button"
                                                className={styles.emojiInlineBtn}
                                                onClick={() => setShowEmoji((prev) => !prev)}
                                                title={t("chat_emoji")}
                                            >
                                                <span className={styles.emojiInlineIcon}>☺</span>
                                            </button>

                                            {showEmoji && (
                                                <div ref={emojiPickerRef} className={styles.emojiPickerPopover}>
                                                    <EmojiPicker
                                                        onEmojiClick={onEmojiClick}
                                                        theme="dark"
                                                        lazyLoadEmojis={true}
                                                        searchDisabled={false}
                                                        skinTonesDisabled={false}
                                                        previewConfig={{ showPreview: false }}
                                                        width={300}
                                                        height={360}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        <button
                                            type="button"
                                            className={styles.sendBtn}
                                            onClick={sendMessage}
                                            disabled={sending || !text.trim()}
                                            title={t("chat_send")}
                                        >
                                            <i className="lni lni-telegram"></i>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}